import {
  ApartmentOutlined,
  AuditOutlined,
  BankOutlined,
  BellOutlined,
  BookOutlined,
  CalculatorOutlined,
  DashboardOutlined,
  DollarOutlined,
  FileTextOutlined,
  FundOutlined,
  LogoutOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Badge, Button, Dropdown, Layout, Menu, Select, Space, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSession } from '../../features/auth/useSession';
import { ERP_MODULES } from '../../features/modules/module-catalog';
import { apiClient } from '../../lib/api-client';
import { useAuthStore } from '../auth/authStore';

const { Header, Sider, Content } = Layout;

const MODULE_ICONS: Record<string, JSX.Element> = {
  sys: <SettingOutlined />,
  iam: <AuditOutlined />,
  mdm: <ApartmentOutlined />,
  gl: <BookOutlined />,
  ap: <FileTextOutlined />,
  ar: <DollarOutlined />,
  tr: <BankOutlined />,
  fa: <CalculatorOutlined />,
  bg: <FundOutlined />,
  pr: <ShoppingCartOutlined />,
  hr: <TeamOutlined />,
  rp: <FundOutlined />,
};

export function AppLayout({ children }: { children: React.ReactNode }): JSX.Element {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: user } = useSession();
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const setActiveCompanyId = useAuthStore((s) => s.setActiveCompanyId);
  const queryClient = useQueryClient();
  const unreadQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ unread: number }>('/notifications/unread-count');
      return (data as { data?: { unread: number } }).data ?? data;
    },
    refetchInterval: 60_000,
  });

  const companyOptions = useMemo(
    () => (user?.companyScope ?? []).map((id) => ({ label: id, value: id })),
    [user],
  );

  const menuItems = [
    { key: '/', icon: <DashboardOutlined />, label: 'Tổng quan' },
    ...ERP_MODULES.map((module) => ({
      key: `/m/${module.code}`,
      icon: MODULE_ICONS[module.code],
      label: `${module.label} (${module.code.toUpperCase()})`,
    })),
  ];

  const selectedKey =
    menuItems.find((i) => location.pathname === i.key || location.pathname.startsWith(`${i.key}/`))
      ?.key ?? '/';

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} theme="dark">
        <div
          style={{
            height: 48,
            margin: 16,
            color: '#fff',
            fontWeight: 700,
            fontSize: collapsed ? 14 : 18,
            textAlign: 'center',
            lineHeight: '48px',
          }}
        >
          {collapsed ? 'ERP' : 'ERP Finance'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            background: '#fff',
            padding: '0 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Space>
            <Typography.Text type="secondary">Công ty:</Typography.Text>
            <Select
              size="small"
              style={{ minWidth: 200 }}
              placeholder="Chọn công ty"
              value={activeCompanyId ?? undefined}
              options={companyOptions}
              notFoundContent="Chưa có công ty trong phạm vi"
              onChange={(companyId) => {
                if (companyId) setActiveCompanyId(companyId);
                void queryClient.invalidateQueries();
              }}
            />
          </Space>
          <Space size="middle">
            <Badge count={unreadQuery.data?.unread ?? 0} overflowCount={99}>
              <Button
                type="text"
                aria-label="Mở thông báo"
                icon={<BellOutlined style={{ fontSize: 18 }} />}
                onClick={() => navigate('/m/sys?view=notifications')}
              />
            </Badge>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: 'Đăng xuất',
                    onClick: async () => {
                      try {
                        await apiClient.post('/auth/logout');
                      } catch (_) {}
                      queryClient.clear();
                      useAuthStore.getState().logout();
                      navigate('/login');
                    },
                  },
                ],
              }}
            >
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} />
                <span>{user?.fullName ?? user?.email ?? 'Người dùng'}</span>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content style={{ margin: 16 }}>{children}</Content>
      </Layout>
    </Layout>
  );
}
